import { useEffect, useMemo, useState } from "react";
import { Activity, Sparkles, Battery, AlertCircle, AlertTriangle, CheckCircle2, Loader2, Zap, Wrench } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const scoreColor = (s) => s >= 75 ? "emerald" : s >= 50 ? "amber" : "rose";
const tones = {
  emerald: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/40", grad: "from-emerald-500 to-emerald-400" },
  amber:   { bg: "bg-amber-500/15",   text: "text-amber-300",   border: "border-amber-500/40",   grad: "from-amber-500 to-amber-400" },
  rose:    { bg: "bg-rose-500/15",    text: "text-rose-300",    border: "border-rose-500/40",    grad: "from-rose-500 to-rose-400" },
};

export default function IoT() {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simBusy, setSimBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/predictive/health-scores");
      setScores(r.data || []);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const simulate = async (car_id, n = 50) => {
    setSimBusy(car_id);
    try {
      await api.post(`/telemetry/simulate/${car_id}?n_records=${n}`);
      toast.success(`${n} record telemetry simulati`);
      load();
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setSimBusy(null); }
  };

  const summary = useMemo(() => {
    const withTel = scores.filter((s) => s.has_telemetry);
    const critical = scores.filter((s) => s.score < 50);
    const warning  = scores.filter((s) => s.score >= 50 && s.score < 75);
    const ok       = scores.filter((s) => s.score >= 75);
    const allAlerts = scores.flatMap((s) => s.alerts || []);
    return {
      total: scores.length,
      monitored: withTel.length,
      critical: critical.length,
      warning: warning.length,
      ok: ok.length,
      alerts: allAlerts.length,
      avgScore: withTel.length > 0 ? Math.round(withTel.reduce((a, s) => a + s.score, 0) / withTel.length) : 100,
    };
  }, [scores]);

  return (
    <div className="space-y-6" data-testid="iot-page">
      <div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
          <Activity className="text-[#ff0033]" size={26} />
          IoT Monitor & Manutenzione Predittiva
        </h1>
        <p className="text-sm text-white/45 mt-1">
          Telemetria OBD-II/GPS · Score salute 0-100 · alert preventivi batteria/gomme/motore via algoritmo ML
        </p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI label="Flotta" value={summary.total} accent="white" icon={Activity} />
        <KPI label="Monitorati" value={`${summary.monitored}/${summary.total}`} accent="sky" icon={Zap} />
        <KPI label="Score medio" value={summary.avgScore} accent={scoreColor(summary.avgScore)} icon={CheckCircle2} />
        <KPI label="Alert attivi" value={summary.alerts} accent="amber" icon={AlertCircle} />
        <KPI label="Critici" value={summary.critical} accent="rose" icon={AlertTriangle} />
        <KPI label="OK" value={summary.ok} accent="emerald" icon={CheckCircle2} />
      </div>

      {/* ALERT BANNER */}
      {summary.alerts > 0 && (
        <div className="glass rounded-2xl p-4 border-l-4 border-rose-500/60 bg-gradient-to-r from-rose-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-rose-300" size={22} />
            <div className="flex-1">
              <div className="font-display font-bold">⚠️ {summary.alerts} alert manutenzione predittiva</div>
              <div className="text-xs text-white/55 mt-1">L'algoritmo ha identificato {summary.critical} veicoli critici e {summary.warning} in osservazione. Pianifica intervento officina.</div>
            </div>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="glass rounded-2xl overflow-hidden">
        {loading && <div className="text-center py-12 text-white/40">Caricamento…</div>}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-xs" data-testid="iot-table">
              <thead className="bg-white/[0.04] text-white/55 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="px-3 py-2 text-left">Veicolo</th>
                  <th className="px-2 py-2 text-center">Score salute</th>
                  <th className="px-2 py-2 text-center">Batteria</th>
                  <th className="px-2 py-2 text-center">Gomme</th>
                  <th className="px-2 py-2 text-center">Motore</th>
                  <th className="px-2 py-2 text-right">Telemetria</th>
                  <th className="px-2 py-2 text-left">Alerts</th>
                  <th className="px-2 py-2 text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((s) => {
                  const tone = tones[scoreColor(s.score)];
                  return (
                    <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.01]">
                      <td className="px-3 py-2">
                        <div className="font-semibold">{s.marca} {s.modello}</div>
                        <div className="text-[10px] font-mono text-white/40">{s.targa}</div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="inline-flex items-center gap-2">
                          <div className={`font-display font-black text-xl ${tone.text}`}>{s.score}</div>
                          <div className="w-12 h-2 bg-white/5 rounded-full overflow-hidden">
                            <div className={`h-full bg-gradient-to-r ${tone.grad}`} style={{ width: `${s.score}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <RiskCell value={s.risk_battery_pct} icon={Battery} label={s.avg_battery_v ? `${s.avg_battery_v}V` : ""} />
                      </td>
                      <td className="px-2 py-2 text-center"><RiskCell value={s.risk_tires_pct} /></td>
                      <td className="px-2 py-2 text-center"><RiskCell value={s.risk_engine_pct} label={s.dtc_count_recent ? `${s.dtc_count_recent} DTC` : ""} /></td>
                      <td className="px-2 py-2 text-right">
                        {s.has_telemetry ? <span className="text-[10px] text-emerald-300 font-mono">{s.samples} pts</span> : <span className="text-[10px] text-white/30">—</span>}
                      </td>
                      <td className="px-2 py-2">
                        {(s.alerts || []).slice(0, 2).map((a, i) => (
                          <div key={i} className={`text-[10px] ${a.level === "critical" ? "text-rose-300" : "text-amber-300"}`}>
                            {a.level === "critical" ? "🔴" : "🟡"} {a.message}
                          </div>
                        ))}
                        {(s.alerts || []).length === 0 && <span className="text-[10px] text-emerald-300">✓ Tutto OK</span>}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => simulate(s.id, 50)} disabled={simBusy === s.id} data-testid={`sim-${s.targa}`}
                            title="Simula telemetria 50 punti"
                            className="p-1.5 rounded hover:bg-sky-500/20 text-sky-300 disabled:opacity-30">
                            {simBusy === s.id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[11px] text-white/40 space-y-1">
        <div>💡 <b>Score salute</b>: 75-100 OK · 50-74 monitorare · &lt;50 critico</div>
        <div>📡 Per attivare un veicolo, click <Sparkles size={11} className="inline text-sky-300" /> per generare 50 punti di telemetria di demo (in produzione: integrazione device OBD-II Teltonika/Queclink)</div>
      </div>
    </div>
  );
}

const RiskCell = ({ value = 0, icon: Icon, label = "" }) => {
  const tone = value >= 60 ? tones.rose : value >= 30 ? tones.amber : tones.emerald;
  return (
    <div className="inline-flex flex-col items-center gap-0.5">
      <div className={`inline-flex items-center gap-1 text-[10px] font-bold ${tone.text}`}>
        {Icon && <Icon size={10} />}
        {value >= 1 ? `${value}%` : "—"}
      </div>
      {label && <div className="text-[9px] text-white/40 font-mono">{label}</div>}
    </div>
  );
};

const KPI = ({ label, value, accent, icon: Icon }) => {
  const t = tones[accent] || { text: "text-white" };
  return (
    <div className="glass rounded-xl p-3 flex items-center gap-2.5">
      <Icon size={18} className={t.text || "text-white/60"} />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest text-white/40 truncate">{label}</div>
        <div className={`font-display font-black text-xl ${t.text || "text-white"} mt-0.5`}>{value}</div>
      </div>
    </div>
  );
};
